import { useState } from 'react';
import { Modal, Form, Col, Row, Spin } from 'antd';

import Button from '../../components/Button/Button';
import FormInput from '../../components/Forms/FormInput/FormInput';
import useLocale from '../../hooks/useLocale';

interface IProps {
  sin?: string | null;
  onSave: (sin: string, onSaveFailure?: () => void) => void;
  onCancel: () => void;
  shouldDisableOnSave?: boolean;
}

interface IForm {
  sin: string;
}

const ChangeSINModal = ({ sin: current, onSave, onCancel, shouldDisableOnSave = false }: IProps) => {
  const { t } = useLocale();
  const [form] = Form.useForm<IForm>();
  const [loading] = useState(false);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [noChanged, setNoChanged] = useState<boolean>(true);

  const handleValuesChange = (val?: string) => {
    if (val !== undefined) setNoChanged(val === (current ?? ''));
  };

  // Validate SIN: digits only, max 12 characters (matching backend validation)
  const validateSIN = (_: any, value: any) => {
    if (!value) return Promise.resolve();

    // Check if only digits
    if (!/^\d+$/.test(value)) {
      return Promise.reject();
    }

    // Check max length
    if (value.length > 12) {
      return Promise.reject();
    }

    return Promise.resolve();
  };

  return (
    <Modal
      destroyOnClose
      centered
      width={440}
      visible
      closable={!loading}
      title={t.ADMIN_INFO_SIN}
      onCancel={onCancel}
      footer={null}
    >
      <Spin spinning={loading}>
        <Form
          layout="vertical"
          form={form}
          onValuesChange={() => handleValuesChange(form.getFieldValue('sin'))}
          onFinish={(values) => {
            if (shouldDisableOnSave) setDisabled(true);
            onSave(values?.sin, () => {
              if (shouldDisableOnSave) setDisabled(false);
            });
          }}
        >
          <FormInput
            type="text"
            name="sin"
            label={t.ADMIN_INFO_SIN}
            initialValue={current ?? ''}
            placeholder={t.SIN_PLACEHOLDER}
            rules={[
              { required: true, message: t.REQUIRED },
              { validator: validateSIN, message: t.SIN_IS_INVALID },
            ]}
          />
          <Row align="middle" justify="end" gutter={10} style={{ paddingTop: 16 }}>
            <Col>
              <Form.Item noStyle>
                <Button kind="cancel" onClick={onCancel}>
                  {t.CANCEL}
                </Button>
              </Form.Item>
            </Col>
            <Col>
              <Form.Item noStyle>
                <Button kind="primary" htmlType="submit" disabled={disabled || noChanged}>
                  {t.SAVE}
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
};

export default ChangeSINModal;
